import React, { useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import classNames from 'classnames';
import {
  Button,
  BUTTON_VARIANTS,
  TablePagination,
  TableContent,
  TableContainer,
  classNamePrefix,
  usePreventTableScrollingRef,
  TextRenderer,
  CELL_RENDERERS,
  preventDefaultBehavior,
  focusLastCell,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
  SearchInput,
  TableHeaderCustomContent,
  INPUT_SIZES
} from 'cdk-radial';

import { Constants } from '../../constants/constants';

import './DataTransactionTable.scss';
import { sendAmplitudeData } from '../../utils/amplitude';
import { AMPLITUDE_EVENTS, AMPLITUDE_PROPERTIES } from '../../constants/amplitudeConstants';


const componentMainClass = `${classNamePrefix}-table`;
const agGridThemeClass = 'ag-theme-balham';
const dataTransactionTableClass = 'data-transaction-table';
const tableClass = classNames(componentMainClass, agGridThemeClass, dataTransactionTableClass);

const frameworkComponentsExtended = {
  textRenderer: TextRenderer
};
const defaultPaginationPageSize = DEFAULT_ROWS_PER_PAGE_OPTIONS[0];
const rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS;

const columns = [
  {
    cellRenderer: CELL_RENDERERS.TEXT,
    field: 'orgName',
    headerName: 'Dealer',
    minWidth: 100,
    width: 150,
  },
  {
    cellRenderer: CELL_RENDERERS.TEXT,
    field: 'orgId',
    headerName: 'Dealer Organization ID',
    minWidth: 100,
    width: 150,
  },
  {
    cellRenderer: CELL_RENDERERS.TEXT,
    field: 'appName',
    headerName: 'App Name',
    minWidth: 100,
    width: 150,
  },
  {
    cellRenderer: CELL_RENDERERS.TEXT,
    field: 'appPublisherName',
    headerName: 'App Publisher',
    minWidth: 100,
    width: 150,
  },
  {
    cellRenderer: CELL_RENDERERS.TEXT,
    field: 'apiName',
    headerName: 'API',
    minWidth: 100,
    width: 150,

  },
  {
    cellRendererFramework: props => (new Date(props.value)).toLocaleString(),
    field: 'lastTransaction',
    headerName: 'Last Transaction Date & Time',
    minWidth: 100,
    width: 200,
  }
];

const getPageSummaryText = (currentPage, totalPages) => {
  return `Page ${currentPage} of ${totalPages}`;
};

const getRowsSummaryText = (currentPage, pageSize, totalRows) => {
  if (totalRows === 0) {
    return `${totalRows}-${totalRows} of ${totalRows}`;
  }
  const rowsFrom = (currentPage - 1) * pageSize + 1;
  const rowsTo = currentPage * pageSize;
  return `${rowsFrom}-${rowsTo > totalRows ? totalRows : rowsTo} of ${totalRows}`;
};

export const DataTransactionTable = ({ dataTransactions, selectedLocations }) => {
  const [gridApi, setGridApi] = useState(null);
  const [currentPage, setCurrentPage] = useState();
  const [totalPages, setTotalPages] = useState();
  const [totalRows, setTotalRows] = useState();
  const [searchInput, setSearchInput] = useState('');

  const tableContentRef = usePreventTableScrollingRef();

  const isAgGridReady = !!gridApi;
  const hasNoRows = totalRows === 0;

  const handleGridReady = gridParams => {
    setGridApi(gridParams.api);
    setCurrentPage(gridParams.api.paginationGetCurrentPage() + 1);
    setTotalPages(gridParams.api.paginationGetTotalPages());
    setTotalRows(gridParams.api.getDisplayedRowCount());
    gridParams.api.sizeColumnsToFit();
  };

  const handlePaginationChanged = () => {
    if (!isAgGridReady) return;
    setCurrentPage(gridApi.paginationGetCurrentPage() + 1);
    setTotalPages(gridApi.paginationGetTotalPages());
    setTotalRows(gridApi.getDisplayedRowCount());
  };

  const paginationPageSize = () => {
    if (isAgGridReady) {
      return gridApi.paginationGetPageSize();
    }
    if (defaultPaginationPageSize.value === DEFAULT_ROWS_PER_PAGE_OPTIONS[0].value) {
      return rowsPerPageOptions[0].value;
    }
    if (rowsPerPageOptions.map(rowPerPageOption => rowPerPageOption === defaultPaginationPageSize.value)) {
      return defaultPaginationPageSize.value;
    }
  };

  const handleTabbingFromPagination = event => {
    const hasRowsToDisplay = gridApi.rowModel.rowsToDisplay.length > 0;
    if (hasRowsToDisplay) {
      preventDefaultBehavior(event);
      focusLastCell(gridApi);
    }
  };

  const handleDownloadClick = () => {
    sendAmplitudeData(AMPLITUDE_EVENTS.DOWNLOAD_TRANSACTION_REPORT, {
      [AMPLITUDE_PROPERTIES.LOCATION_COUNT]: selectedLocations?.length,
      [AMPLITUDE_PROPERTIES.LOCATIONS]: selectedLocations?.map(
        (loc) => ({
          [AMPLITUDE_PROPERTIES.ORG_ID]: loc?.value,
          [AMPLITUDE_PROPERTIES.ORG_NAME]: loc?.label,
        })
      ),
      [AMPLITUDE_PROPERTIES.TRANSACTION_COUNT]: gridApi.getDisplayedRowCount()
    });
    gridApi.exportDataAsCsv({
      fileName: 'data-transaction-report.csv'
    });
  };

  const paginationProps = {
    ariaLabel: 'Pagination',
    buttonLabels: {
      goToFirst: 'Go to first',
      goToLast: 'Go to last',
      goToNext: 'Go to next',
      goToPrevious: 'Go to previous'
    },
    className: 'data-transaction-table-pagination',
    dataTestId: `data-transaction-table-pagination`,
    defaultPaginationPageSize: DEFAULT_ROWS_PER_PAGE_OPTIONS[0],
    gridApi,
    hideBorder: false,
    onTabbingIntoTable: handleTabbingFromPagination,
    pageSizeDropdownId: `data-transaction-table-page-size-dropdown-table`,
    pageSummaryLabelId: `data-transaction-table-pagination-summary-label-table`,
    pageSummaryText: getPageSummaryText(totalPages === 0 ? 0 : currentPage, totalPages),
    rowsPerPageLabel: 'Rows per page:',
    rowsPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS,
    rowsSummaryText: getRowsSummaryText(currentPage, paginationPageSize(), totalRows),
    showGoToFirstLastPage: true
  };

  return <div className="data-transaction-container">
    <TableContainer className={tableClass} >
      <TableHeaderCustomContent
          customHeaderContent={
            <div className='datatransaction-table-header'>
              <div className='datatransaction-table-header-title'>Data Transaction Reports for Selected Dealers</div>
              <SearchInput
                isAlwaysExpanded
                onChange={input => setSearchInput(input)}
                placeholder="Search"
                size={INPUT_SIZES.STANDARD}
                value={searchInput}
                className='datatransaction-search-input'
              />
            </div>
          }
      />
      <TableContent data-testid="data-transaction-table-content" ref={tableContentRef}>
        <AgGridReact
          defaultColDef={{
            width: 100,
            sortable: true,
            resizable: true,
          }}
          frameworkComponents={frameworkComponentsExtended}
          columnDefs={columns}
          onGridReady={handleGridReady}
          rowData={dataTransactions?.filter(({appName, orgName, apiName, orgId, appPublisherName}) => {
            let search = searchInput?.toLowerCase()?.trim();
            return appName.toLowerCase().includes(search) || 
              orgName.toLowerCase().includes(search) ||
              apiName.toLowerCase().includes(search) ||
              orgId.toLowerCase().includes(search) ||
              appPublisherName.toLowerCase().includes(search);
          })}
          onPaginationChanged={handlePaginationChanged}
          pagination={true}
          paginationPageSize={paginationPageSize()}
          paginationAutoPageSize={false}
          suppressPaginationPanel
          rowHeight={42}
        />
      </TableContent>
      {isAgGridReady && <TablePagination defaultPaginationPageSize={defaultPaginationPageSize} {...paginationProps} />}
    </TableContainer>
    <div className="data-transaction-footer">
      <Button
        className="data-transaction-download"
        text={Constants.DOWNLOAD_REPORT}
        variant={BUTTON_VARIANTS.SECONDARY}
        onClick={handleDownloadClick}
        isDisabled={hasNoRows}
      />
    </div>
  </div>
} 